import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API, categoryColor, categoryLabel, verdictColor } from "@/lib/pookieApi";
import { ArrowLeft, Share2, Download, CheckSquare } from "lucide-react";
import { toast } from "sonner";

export default function CasePage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/cases/${id}`)
      .then((r) => setC(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    const shareData = {
      title: `Pookielawyer verdict: ${c?.plaintiff_name || ""} v. ${c?.defendant || ""}`,
      text: `${c?.judgment || ""} — verdict on Pookielawyer.com`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
        // fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Case link copied — go expose them, pookie.");
    } catch {
      toast.error("Could not copy. The clipboard pleaded the 5th.");
    }
  };

  const downloadPdf = () => {
    const url = `${API}/cases/${id}/pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Your official ruling is downloading, pookie.");
  };

  const sharePdf = async () => {
    const pdfUrl = `${API}/cases/${id}/pdf`;
    // Try native share with the PDF file when possible
    if (navigator.canShare && window.fetch) {
      try {
        const resp = await fetch(pdfUrl);
        const blob = await resp.blob();
        const file = new File([blob], `Pookielawyer-${c?.case_number || "ruling"}.pdf`, {
          type: "application/pdf",
        });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `Pookielawyer verdict — ${c?.case_number}`,
            text: c?.judgment || "Your official ruling",
            files: [file],
          });
          return;
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        // fall through to link fallback
      }
    }
    // Fallback: copy the PDF link so users can paste anywhere
    try {
      await navigator.clipboard.writeText(pdfUrl);
      toast.success("PDF link copied — paste it anywhere, pookie.");
    } catch {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 font-mono-pl" data-testid="case-loading">
        Fetching case from the clerk…
      </div>
    );
  }
  if (notFound || !c) {
    return (
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-16" data-testid="case-notfound">
        <h1 className="font-display text-4xl">Case file missing.</h1>
        <p className="font-mono-pl mt-3 text-black/70">
          This case has been sealed, expunged, or simply ghosted us.
        </p>
        <Link to="/archive" className="pl-btn pl-btn-secondary mt-6">Back to Archive</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-10">
      <Link to="/archive" className="font-mono-pl text-sm inline-flex items-center gap-2 mb-6" data-testid="case-back">
        <ArrowLeft size={16} strokeWidth={3} /> Back to archive
      </Link>

      <div className="pl-card bg-white p-6 md:p-10" data-testid="case-detail">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="pl-chip font-mono-pl">{c.case_number}</span>
            <span className={`pl-chip ${categoryColor(c.category)}`}>
              {categoryLabel(c.category)}
            </span>
            <span className="pl-chip">Drama: {c.drama_score}/10</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={downloadPdf} className="pl-btn pl-btn-secondary text-xs" data-testid="case-download-pdf">
              <Download size={14} strokeWidth={3} /> PDF
            </button>
            <button onClick={sharePdf} className="pl-btn pl-btn-mint text-xs" data-testid="case-share-pdf">
              <Share2 size={14} strokeWidth={3} /> Share PDF
            </button>
            <button onClick={share} className="pl-btn pl-btn-ghost text-xs" data-testid="case-share">
              <Share2 size={14} strokeWidth={3} /> Link
            </button>
          </div>
        </div>

        <div className="mt-6">
          <div className="pl-label">Parties</div>
          <div className="font-display text-3xl md:text-4xl mt-1">
            {c.plaintiff_name} <span className="text-black/40">v.</span> {c.defendant}
          </div>
        </div>

        <div className="mt-8 pl-card-flat bg-[#FFF5D6] p-5">
          <div className="pl-label mb-2">The Tea (Plaintiff's Statement)</div>
          <p className="font-mono-pl text-sm md:text-base whitespace-pre-wrap leading-relaxed">{c.tea}</p>
        </div>

        {c.demands && (
          <div className="mt-4 pl-card-flat bg-[#A0E7E5] p-5">
            <div className="pl-label mb-2">Demands</div>
            <p className="font-mono-pl text-sm md:text-base whitespace-pre-wrap">{c.demands}</p>
          </div>
        )}

        <div className="mt-10 text-center">
          <div className="pl-label">Final Verdict</div>
          <div className="mt-3 inline-block">
            <span className={`pl-stamp pl-stamp-animate text-2xl md:text-4xl ${verdictColor(c.verdict)}`}>
              {c.verdict}
            </span>
          </div>
        </div>

        <div className="mt-8 pl-card-flat bg-white p-5 md:p-6 border-dashed">
          <div className="pl-label mb-3">Judgment</div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight">{c.judgment}</h2>
        </div>

        <div className="mt-5 pl-card-flat bg-white p-5 md:p-6">
          <div className="pl-label mb-3">Court Reasoning</div>
          <p className="font-mono-pl text-sm md:text-base whitespace-pre-wrap leading-relaxed">{c.reasoning}</p>
        </div>

        <div className="mt-5 pl-card-flat bg-[#B4F8C8] p-5 md:p-6">
          <div className="pl-label mb-3">Sentence</div>
          <p className="font-mono-pl text-base md:text-lg">{c.sentence}</p>
        </div>

        {c.tasks && c.tasks.length > 0 && (
          <div className="mt-5 pl-card-flat bg-[#FFAEBC] p-5 md:p-6" data-testid="case-tasks">
            <div className="pl-label mb-3 flex items-center gap-2">
              <CheckSquare size={14} strokeWidth={3} /> Court-Ordered Tasks
            </div>
            <ul className="space-y-3">
              {c.tasks.map((t, idx) => (
                <li key={idx} className="flex items-start gap-3 font-mono-pl text-sm md:text-base" data-testid={`case-task-${idx}`}>
                  <span className="mt-1 w-5 h-5 flex-none rounded-md border-[3px] border-black bg-white" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="font-mono-pl text-xs mt-4 text-black/60">
              Tip: download the PDF, print it, actually check the boxes. Extremely legally-not-binding.
            </p>
          </div>
        )}
      </div>

      <div className="mt-8 text-center">
        <Link to="/file" className="pl-btn pl-btn-primary" data-testid="case-file-new">
          File another case
        </Link>
      </div>
    </div>
  );
}
